import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Textarea } from './Textarea';

export interface ExpressionEditorProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** The equation's identity line: its id, its label and any per-equation controls. */
  header?: ReactNode;
  /** The result of the last local evaluation — muted, because it is expected to be there. */
  result?: ReactNode;
  /** What is wrong with the expression, in the one colour the kit spends on problems. */
  error?: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
  rows?: number;
  className?: string;
}

/**
 * One equation, edited as text (plan §2.6).
 *
 * An expression is code, so the box is monospaced and left-to-right even in Arabic. Underneath it sits
 * exactly one line: the number this expression currently produces, or — when it will not compile — what
 * is wrong with it. The two are never shown together, because a stale result beside an error reads as
 * if the error did not matter.
 */
export const ExpressionEditor = ({
  id,
  value,
  onChange,
  header,
  result,
  error,
  hint,
  disabled = false,
  rows = 2,
  className,
}: ExpressionEditorProps) => (
  <div className={cn('space-y-1.5', className)}>
    {header}
    <Textarea
      id={id}
      dir="ltr"
      spellCheck={false}
      autoComplete="off"
      rows={rows}
      value={value}
      disabled={disabled}
      hasError={Boolean(error)}
      onChange={(event) => onChange(event.target.value)}
      className="resize-y font-mono text-sm leading-relaxed"
    />
    {error ? (
      <p
        role="alert"
        className="text-xs text-destructive"
      >
        {error}
      </p>
    ) : result !== undefined && result !== null ? (
      <p className="font-mono text-xs tabular-nums text-muted-foreground">{result}</p>
    ) : null}
    {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
  </div>
);
